import React, { useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'

import { useFetchBrandInfo } from '@@partner:graphql/query/brand/currentBrandInfo.gql'
import { useMutateUpdateMobileAppConfig } from '@@partner:graphql/mutation/brand/updateMobileAppConfig.gql'

import { 
  Panel, 
  Nav, 
  Button, 
  ButtonToolbar, 
  Form, 
  Schema, 
  Modal, 
  InputGroup, 
  Input,
  Checkbox,
  ButtonGroup,
  IconButton,
  Message,
  Placeholder,
  Tabs,
  TagGroup,
  Tag,
  SelectPicker
} from 'rsuite'

import PlusIcon from '@rsuite/icons/Plus'
import TrashIcon from '@rsuite/icons/Trash'
import EditIcon from '@rsuite/icons/Edit'

import { notifyOk, notifyError } from '@@partner:helpers/notification'


// Import types from mobileAppConfig.ts
import { 
  CancelReasonInput, 
  InAppMessageInput,
  AlertButtonInput,
  AlertInput,
  AssemblerMobileAppConfig,
  MessageType
} from '@@partner:types:app'

// Form validation schema
const validationModel = Schema.Model({
  text: Schema.Types.StringType().isRequired('Это поле обязательно'),
  title: Schema.Types.StringType().isRequired('Это поле обязательно'),
  type: Schema.Types.StringType().isRequired('Это поле обязательно'),
  minimalVersion: Schema.Types.StringType().isRequired('Это поле обязательно')
})

const ALERT_TYPES: {
  label: string,
  value: MessageType
}[] = [
  {
    label: 'Информация',
    value: 'info'
  },
  {
    label: 'Предупреждение',
    value: 'warning'
  },
  {
    label: 'Критиченское',
    value: 'error'
  }
]

export default function AssemblingSettingsPage () {
  const { brandSlug } = useParams<any>()
  const { data, loading, refetch } = useFetchBrandInfo()
  const [ updateMobileAppConfig, { loading: updating } ] = useMutateUpdateMobileAppConfig()
  
  // Form modals state
  const [ reasonModalOpen, setReasonModalOpen ] = useState(false)
  const [ messageModalOpen, setMessageModalOpen ] = useState(false)
  const [ alertModalOpen, setAlertModalOpen ] = useState(false)
  
  // Current editing item or null for creating new
  const [ currentEditItem, setCurrentEditItem ] = useState<{index: number, type: 'position' | 'order'} | null>(null)
  
  // Form values state
  const [ reasonForm, setReasonForm ] = useState<CancelReasonInput>({
    text: '',
    canComment: false,
    requireComment: false
  })
  
  const [ messageForm, setMessageForm ] = useState<InAppMessageInput>({
    type: 'info' as MessageType,
    text: ''
  })
  
  const [ alertForm, setAlertForm ] = useState<AlertInput>({
    title: '',
    text: '',
    restrictAccessToApp: false,
    button: {
      text: '',
      action: '',
      actionData: ''
    }
  })

  const [ activeTab, setActiveTab ] = useState('position-reasons')
  const [ reasonsTab, setReasonsTab ] = useState('position')
  
  // Обработчики для компонента Tabs
  const handleTabChange = useCallback((eventKey: string) => {
    setActiveTab(eventKey)
  }, [])
  
  const handleReasonsTabChange = useCallback((eventKey: string) => {
    setReasonsTab(eventKey)
  }, [])

  // Reset form and close modal handlers с использованием useCallback
  const closeReasonModal = useCallback(() => {
    setReasonForm({
      text: '',
      canComment: false,
      requireComment: false
    })
    setCurrentEditItem(null)
    setReasonModalOpen(false)
  }, [])
  
  const closeMessageModal = useCallback(() => {
    setMessageForm({
      type: 'info' as MessageType,
      text: ''
    })
    setCurrentEditItem(null)
    setMessageModalOpen(false)
  }, [])
  
  const closeAlertModal = useCallback(() => {
    setAlertForm({
      title: '',
      text: '',
      restrictAccessToApp: false,
      button: {
        text: '',
        action: '',
        actionData: ''
      }
    })
    setCurrentEditItem(null)
    setAlertModalOpen(false)
  }, [])

  // Edit item handlers
  const editReason = (reason: CancelReasonInput, index: number, type: 'position' | 'order') => {
    setReasonForm({...reason})
    setCurrentEditItem({index, type})
    setReasonModalOpen(true)
  }
  
  const editMessage = (message: InAppMessageInput, index: number) => {
    setMessageForm({...message})
    setCurrentEditItem({index, type: 'position'}) // Type doesn't matter for messages
    setMessageModalOpen(true)
  }
  
  const editAlert = (alert: AlertInput, index: number) => {
    setAlertForm({
      ...alert,
      button: alert.button || {
        text: '',
        action: '',
        actionData: ''
      }
    })
    setCurrentEditItem({index, type: 'position'}) // Type doesn't matter for alerts
    setAlertModalOpen(true)
  }

  // Универсальная функция для обновления конфигурации
  const updateConfig = useCallback((
    updater: (config: AssemblerMobileAppConfig) => AssemblerMobileAppConfig, 
    successMessage: string, 
    errorMessage: string
  ) => {
    if (!data?.brandInfo?.assemblerConfig) return Promise.reject(new Error('Конфигурация не найдена'))
    
    const updatedConfig = updater({...data.brandInfo.assemblerConfig})
    
    return updateMobileAppConfig({
      variables: {
        mobileAppConfig: {
          assemblerConfig: updatedConfig
        }
      }
    })
      .then(() => {
        notifyOk(successMessage)
        refetch()
        return updatedConfig
      })
      .catch((error) => {
        notifyError(errorMessage)
        throw error
      })
  }, [ data, updateMobileAppConfig, refetch ])
  
  // Delete item handlers с использованием универсальной функции
  const deleteReason = useCallback((index: number, type: 'position' | 'order') => {
    const key = type === 'position' ? 'positionCancelReasons' : 'orderCancelReasons'
    
    updateConfig(
      (config) => ({
        ...config,
        [key]: [
          ...config[key].slice(0, index),
          ...config[key].slice(index + 1)
        ]
      }),
      'Причина удалена',
      'Ошибка при удалении причины'
    )
  }, [ updateConfig ])
  
  const deleteMessage = useCallback((index: number) => {
    updateConfig(
      (config) => ({
        ...config,
        inAppMessages: [
          ...config.inAppMessages.slice(0, index),
          ...config.inAppMessages.slice(index + 1)
        ]
      }),
      'Сообщение удалено',
      'Ошибка при удалении сообщения'
    )
  }, [ updateConfig ])
  
  const deleteAlert = useCallback((index: number) => {
    updateConfig(
      (config) => ({
        ...config,
        alerts: [
          ...config.alerts.slice(0, index),
          ...config.alerts.slice(index + 1)
        ]
      }),
      'Уведомление удалено',
      'Ошибка при удалении уведомления'
    )
  }, [ updateConfig ])

  // Save item handlers с использованием универсальной функции
  const saveReason = useCallback((type: 'position' | 'order') => {
    const key = type === 'position' ? 'positionCancelReasons' : 'orderCancelReasons'
    
    updateConfig(
      (config) => {
        const updatedConfig = { ...config }
        
        if (currentEditItem !== null) {
          updatedConfig[key] = [
            ...updatedConfig[key].slice(0, currentEditItem.index),
            reasonForm,
            ...updatedConfig[key].slice(currentEditItem.index + 1)
          ]
        } 
        else {
          updatedConfig[key] = [
            ...updatedConfig[key],
            reasonForm
          ]
        }
        
        return updatedConfig
      },
      `Причина ${currentEditItem !== null ? 'обновлена' : 'добавлена'}`,
      'Ошибка при сохранении причины'
    ).then(() => {
      closeReasonModal()
    })
  }, [ updateConfig, reasonForm, currentEditItem, closeReasonModal ])
  
  const saveMessage = useCallback(() => {
    updateConfig(
      (config) => {
        const updatedConfig = { ...config }
        
        if (currentEditItem !== null) {
          updatedConfig.inAppMessages = [
            ...updatedConfig.inAppMessages.slice(0, currentEditItem.index),
            messageForm,
            ...updatedConfig.inAppMessages.slice(currentEditItem.index + 1)
          ]
        } 
        else {
          updatedConfig.inAppMessages = [
            ...updatedConfig.inAppMessages,
            messageForm
          ]
        }
        
        return updatedConfig
      },
      `Сообщение ${currentEditItem !== null ? 'обновлено' : 'добавлено'}`,
      'Ошибка при сохранении сообщения'
    ).then(() => {
      closeMessageModal()
    })
  }, [ updateConfig, messageForm, currentEditItem, closeMessageModal ])
  
  const saveAlert = useCallback(() => {
    const formattedAlert = { ...alertForm }
    
    // Remove empty button if all fields are empty
    if (!alertForm.button?.text && !alertForm.button?.action && !alertForm.button?.actionData) {
      delete formattedAlert.button
    }
    
    updateConfig(
      (config) => {
        const updatedConfig = { ...config }
        
        if (currentEditItem !== null) {
          updatedConfig.alerts = [
            ...updatedConfig.alerts.slice(0, currentEditItem.index),
            formattedAlert,
            ...updatedConfig.alerts.slice(currentEditItem.index + 1)
          ]
        } 
        else {
          updatedConfig.alerts = [
            ...updatedConfig.alerts,
            formattedAlert
          ]
        }
        
        return updatedConfig
      },
      `Уведомление ${currentEditItem !== null ? 'обновлено' : 'добавлено'}`,
      'Ошибка при сохранении уведомления'
    ).then(() => {
      closeAlertModal()
    })
  }, [ updateConfig, alertForm, currentEditItem, closeAlertModal ])

  // Save minimal version используя универсальную функцию
  const saveMinimalVersion = useCallback((version: string) => {
    updateConfig(
      (config) => ({ ...config, minimalVersion: version }),
      'Минимальная версия обновлена',
      'Ошибка при обновлении минимальной версии'
    )
  }, [ updateConfig ])

  // If we're still loading the data, show a placeholder
  if (loading) {
    return (
      <div className="AssemblingSettingsPage">
        <h1 className="u-heading">Настройки сборки</h1>
        <Panel bordered>
          <Placeholder.Paragraph rows={6} active />
        </Panel>
      </div>
    )
  }

  const assemblerConfig: AssemblerMobileAppConfig = data?.brandInfo?.assemblerConfig || {
    minimalVersion: '',
    positionCancelReasons: [],
    orderCancelReasons: [],
    inAppMessages: [],
    alerts: []
  }

  return (
    <div className="AssemblingSettingsPage">
      <h1 className="u-heading">Настройки сборки</h1>
      
      <Tabs activeKey={activeTab} onSelect={handleTabChange} appearance="subtle">
        <Tabs.Tab eventKey="position-reasons" title="Причины замен/отмен">
          <Panel bordered>
            <Tabs appearance="tabs" activeKey={reasonsTab} onSelect={handleReasonsTabChange}>
              <Tabs.Tab eventKey="position" title="Позиция">
                <div className="reason-list">
                  {assemblerConfig.positionCancelReasons.length > 0 ? (
                    assemblerConfig.positionCancelReasons.map((reason: CancelReasonInput, index: number) => (
                      <Panel bordered key={`position-${index}`} style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <h4>{reason.text}</h4>
                            <div>
                              {reason.canComment && <Tag color="blue">Можно комментировать</Tag>}
                              {reason.requireComment && <Tag color="red">Обязательный комментарий</Tag>}
                            </div>
                          </div>
                          <ButtonToolbar>
                            <IconButton 
                              icon={<EditIcon />} 
                              appearance="subtle" 
                              onClick={() => editReason(reason, index, 'position')}
                            />
                            <IconButton 
                              icon={<TrashIcon />} 
                              appearance="subtle" 
                              color="red" 
                              onClick={() => deleteReason(index, 'position')}
                            />
                          </ButtonToolbar>
                        </div>
                      </Panel>
                    ))
                  ) : (
                    <Message type="info">
                      Нет добавленных причин замены позиции
                    </Message>
                  )}
                  
                  <Button 
                    appearance="primary" 
                    color="green" 
                    startIcon={<PlusIcon />}
                    onClick={() => {
                      setReasonForm({
                        text: '',
                        canComment: false,
                        requireComment: false
                      })
                      setCurrentEditItem(null)
                      setReasonModalOpen(true)
                    }}
                    style={{ marginTop: 10 }}
                  >
                    Добавить причину
                  </Button>
                </div>
              </Tabs.Tab>
              
              <Tabs.Tab eventKey="order" title="Заказ">
                <div className="reason-list">
                  {assemblerConfig.orderCancelReasons.length > 0 ? (
                    assemblerConfig.orderCancelReasons.map((reason: CancelReasonInput, index: number) => (
                      <Panel bordered key={`order-${index}`} style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <h4>{reason.text}</h4>
                            <div>
                              {reason.canComment && <Tag color="blue">Можно комментировать</Tag>}
                              {reason.requireComment && <Tag color="red">Обязательный комментарий</Tag>}
                            </div>
                          </div>
                          <ButtonToolbar>
                            <IconButton 
                              icon={<EditIcon />} 
                              appearance="subtle" 
                              onClick={() => editReason(reason, index, 'order')}
                            />
                            <IconButton 
                              icon={<TrashIcon />} 
                              appearance="subtle" 
                              color="red" 
                              onClick={() => deleteReason(index, 'order')}
                            />
                          </ButtonToolbar>
                        </div>
                      </Panel>
                    ))
                  ) : (
                    <Message type="info">
                      Нет добавленных причин отмены заказа
                    </Message>
                  )}
                  
                  <Button 
                    appearance="primary" 
                    color="green" 
                    startIcon={<PlusIcon />}
                    onClick={() => {
                      setReasonForm({
                        text: '',
                        canComment: false,
                        requireComment: false
                      })
                      setCurrentEditItem(null)
                      setReasonModalOpen(true)
                    }}
                    style={{ marginTop: 10 }}
                  >
                    Добавить причину
                  </Button>
                </div>
              </Tabs.Tab>
            </Tabs>
          </Panel>
        </Tabs.Tab>
        
        <Tabs.Tab eventKey="notifications" title="Уведомления">
          <Panel bordered>
            <div className="message-list">
              {assemblerConfig.inAppMessages.length > 0 ? (
                assemblerConfig.inAppMessages.map((message: InAppMessageInput, index: number) => (
                  <Panel bordered key={`message-${index}`} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h4>{message.text}</h4>
                        <Tag color="violet">Тип: {message.type}</Tag>
                      </div>
                      <ButtonToolbar>
                        <IconButton 
                          icon={<EditIcon />} 
                          appearance="subtle" 
                          onClick={() => editMessage(message, index)}
                        />
                        <IconButton 
                          icon={<TrashIcon />} 
                          appearance="subtle" 
                          color="red" 
                          onClick={() => deleteMessage(index)}
                        />
                      </ButtonToolbar>
                    </div>
                  </Panel>
                ))
              ) : (
                <Message type="info">
                  Нет добавленных сообщений
                </Message>
              )}
              
              <Button 
                appearance="primary" 
                color="green" 
                startIcon={<PlusIcon />}
                onClick={() => {
                  setMessageForm({
                    type: 'info',
                    text: ''
                  })
                  setCurrentEditItem(null)
                  setMessageModalOpen(true)
                }}
                style={{ marginTop: 10 }}
              >
                Добавить сообщение
              </Button>
            </div>
          </Panel>
        </Tabs.Tab>
        
        <Tabs.Tab eventKey="alerts" title="Алерты">
          <Panel bordered>
            <div className="alert-list">
              {assemblerConfig.alerts.length > 0 ? (
                assemblerConfig.alerts.map((alert: AlertInput, index: number) => (
                  <Panel bordered key={`alert-${index}`} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h4>{alert.title}</h4>
                        <p>{alert.text}</p>
                        <div>
                          {alert.restrictAccessToApp && <Tag color="red">Ограничение доступа к приложению</Tag>}
                          {alert.button && <Tag color="green">С кнопкой: {alert.button.text}</Tag>}
                        </div>
                        {alert.button && (
                          <div style={{ marginTop: 10 }}>
                            <small>
                              <strong>Действие:</strong> {alert.button.action}
                              {alert.button.actionData && (
                                <> | <strong>Данные:</strong> {alert.button.actionData}</>
                              )}
                            </small>
                          </div>
                        )}
                      </div>
                      <ButtonToolbar>
                        <IconButton 
                          icon={<EditIcon />} 
                          appearance="subtle" 
                          onClick={() => editAlert(alert, index)}
                        />
                        <IconButton 
                          icon={<TrashIcon />} 
                          appearance="subtle" 
                          color="red" 
                          onClick={() => deleteAlert(index)}
                        />
                      </ButtonToolbar>
                    </div>
                  </Panel>
                ))
              ) : (
                <Message type="info">
                  Нет добавленных алертов
                </Message>
              )}
              
              <Button 
                appearance="primary" 
                color="green" 
                startIcon={<PlusIcon />}
                onClick={() => {
                  setAlertForm({
                    title: '',
                    text: '',
                    restrictAccessToApp: false,
                    button: {
                      text: '',
                      action: '',
                      actionData: ''
                    }
                  })
                  setCurrentEditItem(null)
                  setAlertModalOpen(true)
                }}
                style={{ marginTop: 10 }}
              >
                Добавить алерт
              </Button>
            </div>
          </Panel>
        </Tabs.Tab>
        
        <Tabs.Tab eventKey="versions" title="Версии">
          <Panel bordered>
            <Form fluid>
              <Form.Group>
                <Form.ControlLabel>Минимальная версия приложения</Form.ControlLabel>
                <InputGroup>
                  <Input 
                    value={assemblerConfig.minimalVersion} 
                    onChange={value => saveMinimalVersion(value)} 
                    placeholder="Например: 1.0.0"
                  />
                </InputGroup>
                <Form.HelpText>
                  Пользователи с версией ниже указанной получат уведомление о необходимости обновления приложения
                </Form.HelpText>
              </Form.Group>
            </Form>
          </Panel>
        </Tabs.Tab>
      </Tabs>
      
      {/* Modal for adding/editing reasons */}
      <Modal open={reasonModalOpen} onClose={closeReasonModal}>
        <Modal.Header>
          <Modal.Title>{currentEditItem !== null ? 'Редактирование' : 'Добавление'} причины</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form fluid model={validationModel}>
            <Form.Group>
              <Form.ControlLabel>Текст причины</Form.ControlLabel>
              <Form.Control 
                name="text"
                value={reasonForm.text} 
                onChange={value => setReasonForm({...reasonForm, text: value})}
              />
            </Form.Group>
            
            <Form.Group>
              <Form.ControlLabel>Настройки комментирования</Form.ControlLabel>
              <div style={{ marginTop: 10 }}>
                <Checkbox 
                  checked={reasonForm.canComment} 
                  onChange={(_, checked) => setReasonForm({...reasonForm, canComment: checked})}
                >
                  Можно добавить комментарий
                </Checkbox>
              </div>
              
              <div style={{ marginTop: 10 }}>
                <Checkbox 
                  checked={reasonForm.requireComment} 
                  onChange={(_, checked) => setReasonForm({...reasonForm, requireComment: checked})}
                  disabled={!reasonForm.canComment}
                >
                  Комментарий обязателен
                </Checkbox>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            onClick={() => saveReason(currentEditItem?.type as ('position' | 'order') || reasonsTab as ('position' | 'order'))}
            appearance="primary" 
            disabled={!reasonForm.text}
          >
            Сохранить
          </Button>
          <Button onClick={closeReasonModal} appearance="subtle">
            Отмена
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Modal for adding/editing messages */}
      <Modal open={messageModalOpen} onClose={closeMessageModal}>
        <Modal.Header>
          <Modal.Title>{currentEditItem !== null ? 'Редактирование' : 'Добавление'} сообщения</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form fluid model={validationModel}>
            <Form.Group>
              <Form.ControlLabel>Тип сообщения</Form.ControlLabel>
              <SelectPicker
                data={ALERT_TYPES}
                value={messageForm.type}
                onChange={value => setMessageForm({...messageForm, type: (value ?? 'info')})}
                searchable={false}
                placeholder="Тип сообщения"
                valueKey="value"
                labelKey="label"
                cleanable={false}
              />
            </Form.Group>
            
            <Form.Group>
              <Form.ControlLabel>Текст сообщения</Form.ControlLabel>
              <Form.Control 
                name="text"
                value={messageForm.text} 
                onChange={value => setMessageForm({...messageForm, text: value})}
                componentClass="textarea"
                rows={3}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={saveMessage} appearance="primary" disabled={!messageForm.type || !messageForm.text}>
            Сохранить
          </Button>
          <Button onClick={closeMessageModal} appearance="subtle">
            Отмена
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Modal for adding/editing alerts */}
      <Modal open={alertModalOpen} onClose={closeAlertModal}>
        <Modal.Header>
          <Modal.Title>{currentEditItem !== null ? 'Редактирование' : 'Добавление'} алерта</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form fluid model={validationModel}>
            <Form.Group>
              <Form.ControlLabel>Заголовок</Form.ControlLabel>
              <Form.Control 
                name="title"
                value={alertForm.title} 
                onChange={value => setAlertForm({...alertForm, title: value})}
              />
            </Form.Group>
            
            <Form.Group>
              <Form.ControlLabel>Текст уведомления</Form.ControlLabel>
              <Form.Control 
                name="text"
                value={alertForm.text} 
                onChange={value => setAlertForm({...alertForm, text: value})}
                componentClass="textarea"
                rows={3}
              />
            </Form.Group>
            
            <Form.Group>
              <Form.ControlLabel>Настройки</Form.ControlLabel>
              <div style={{ marginTop: 10 }}>
                <Checkbox 
                  checked={alertForm.restrictAccessToApp} 
                  onChange={(_, checked) => setAlertForm({...alertForm, restrictAccessToApp: checked})}
                >
                  Ограничить доступ к приложению
                </Checkbox>
              </div>
            </Form.Group>
            
            <Form.Group>
              <Form.ControlLabel>Кнопка (необязательно)</Form.ControlLabel>
              <InputGroup style={{marginBottom: 10}}>
                <InputGroup.Addon>Текст</InputGroup.Addon>
                <Input 
                  value={alertForm.button?.text || ''} 
                  onChange={value => setAlertForm({
                    ...alertForm, 
                    button: {
                      ...alertForm.button,
                      text: value
                    }
                  })}
                  placeholder="Текст на кнопке"
                />
              </InputGroup>
              
              <InputGroup style={{marginBottom: 10}}>
                <InputGroup.Addon>Действие</InputGroup.Addon>
                <Input 
                  value={alertForm.button?.action || ''} 
                  onChange={value => setAlertForm({
                    ...alertForm, 
                    button: {
                      ...alertForm.button,
                      action: value
                    }
                  })}
                  placeholder="Например: open_url"
                />
              </InputGroup>
              
              <InputGroup>
                <InputGroup.Addon>Данные</InputGroup.Addon>
                <Input 
                  value={alertForm.button?.actionData || ''} 
                  onChange={value => setAlertForm({
                    ...alertForm, 
                    button: {
                      ...alertForm.button,
                      actionData: value
                    }
                  })}
                  placeholder="Например: URL адрес"
                />
              </InputGroup>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            onClick={saveAlert} 
            appearance="primary" 
            disabled={!alertForm.title || !alertForm.text}
          >
            Сохранить
          </Button>
          <Button onClick={closeAlertModal} appearance="subtle">
            Отмена
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
